import React, { useState, useEffect, useCallback } from 'react';
import { useHistory } from "react-router-dom";
import axios from "../../utils/axios";
import Swal from 'sweetalert2';
import Moment from 'moment';
import { getToken, getUser } from '../../utils/Common';
import DataTable from 'react-data-table-component';
import Layout from '../../components/Layout';
import { Image, Transformation } from 'cloudinary-react';
import { FileText, Clock, ClipboardCheck, CheckCircle, CheckCircle2, XCircle, AlertCircle, Download } from 'lucide-react';
import '../../styles/datatable.css';
import * as XLSX from 'xlsx-js-style';

function Proposal() {
  const token = getToken();
  const headers = { 
    Authorization: `Bearer ${token}`,
    'Access-Control-Allow-Origin': '*', 
    'Content-Type': 'application/json' 
  };
  const history = useHistory();
  
  // Session
  const userData = getUser() || {};
  const udeptID = userData.dept_id;
  const hcode = userData.hotel_id;
  const role = userData.role_name;

  // States
  const [purchaseProposal, setPurchaseProposal] = useState([]);
  const [hotelList, setHotelList] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState(hcode);
  const [loading, setLoading] = useState(false);
  const [filterText, setFilterText] = useState('');
  const [statusFilter, setStatusFilter] = useState(null); // null = show all, 1,2,3,22,33 = filter by status
  const [expandedRows, setExpandedRows] = useState({});
  const [loadingItems, setLoadingItems] = useState({});

  // Custom styles for DataTable
  const customStyles = {
    table: {
      style: {
        backgroundColor: 'white',
        borderRadius: '12px',
      },
    },
    tableWrapper: {
      style: {
        display: 'table',
        width: '100%',
      },
    },
    headRow: {
      style: {
        backgroundColor: '#f3f4f6',
        borderBottomWidth: '2px',
        borderBottomColor: '#e5e7eb',
        borderBottomStyle: 'solid',
        minHeight: '52px',
      },
    },
    headCells: {
      style: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#374151',
        paddingLeft: '16px',
        paddingRight: '16px',
        paddingTop: '12px',
        paddingBottom: '12px',
      },
    },
    rows: {
      style: {
        fontSize: '12px',
        color: '#1f2937',
        minHeight: '48px',
        cursor: 'pointer',
        '&:not(:last-of-type)': {
          borderBottomStyle: 'solid',
          borderBottomWidth: '1px',
          borderBottomColor: '#e5e7eb',
        },
        '&:hover': {
          backgroundColor: '#f9fafb',
          cursor: 'pointer',
        },
      },
    },
    cells: {
      style: {
        paddingLeft: '16px',
        paddingRight: '16px',
        paddingTop: '12px',
        paddingBottom: '12px',
      },
    },
    pagination: {
      style: {
        borderTop: '1px solid #e5e7eb',
        minHeight: '56px',
      },
    },
  };

  // Fetch proposal items
  const fetchProposalItems = useCallback(async (ppId) => {

    if (expandedRows[ppId]) {

      return; // Already loaded
    }
    
    setLoadingItems(prev => ({...prev, [ppId]: true}));
    
    try {

      const response = await axios.get(`/proposal/items/${ppId}`, { headers });

      if (response.data && response.data.status === 200) {


        setExpandedRows(prev => ({
          ...prev,
          [ppId]: response.data.data
        }));
      } else {

      }
    } catch (error) {


      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.response?.data?.message || 'Failed to load items. Please try again.',
      });
    } finally {
      setLoadingItems(prev => ({...prev, [ppId]: false}));
    }
  }, [expandedRows, headers]);

  // Expandable Component to show items
  const ExpandedComponent = ({ data }) => {
    const items = expandedRows[data.id_pp];
    const isLoading = loadingItems[data.id_pp];

    if (isLoading) {
      return (
        <div className="flex justify-center items-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
          <span className="ml-3 text-gray-600">Loading items...</span>
        </div>
      );
    }

    if (!items || items.length === 0) {
      return (
        <div className="text-center py-8 text-gray-500">
          No items found for this proposal
        </div>
      );
    }

    return (
      <div className="px-3 py-3 bg-gray-50">
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-100">
              <tr>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-700 uppercase">Item Name</th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-700 uppercase">Qty</th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-700 uppercase">Unit Price</th>
                <th className="px-3 py-2 text-left text-[10px] font-medium text-gray-700 uppercase">Total</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {items.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50">
                  <td className="px-3 py-2 text-xs text-gray-900">
                    <div className="flex items-center space-x-2">
                      {item.img_item ? (
                        <div className="w-8 h-8 rounded-full overflow-hidden flex-shrink-0">
                          <Image 
                            cloudName="cloud-prima-hotel-indonesia" 
                            publicId={item.img_item}
                            className="w-full h-full object-cover"
                            style={{width: '32px', height: '32px'}}
                          >
                            <Transformation width="64" height="64" crop="fill" gravity="face" />
                          </Image>
                        </div>
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                          <span className="text-gray-500 text-[10px] font-medium">
                            {item.nama_item ? item.nama_item.charAt(0).toUpperCase() : '?'}
                          </span>
                        </div>
                      )}
                      <span className="font-medium text-xs">{item.nama_item || 'Unknown Item'}</span>
                    </div>
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-700">
                    {item.quantity ? `${item.quantity} ${item.unit || ''}` : '-'}
                  </td>
                  <td className="px-3 py-2 text-xs text-gray-700">
                    {item.quotation_price ? `IDR ${parseInt(item.quotation_price).toLocaleString('id-ID')}` : '-'}
                  </td>
                  <td className="px-3 py-2 text-xs font-medium text-gray-900">
                    {item.quotation_total ? `IDR ${parseInt(item.quotation_total).toLocaleString('id-ID')}` : '-'}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50">
              <tr>
                <td colSpan="3" className="px-3 py-2 text-xs font-medium text-gray-900 text-right">
                  Total:
                </td>
                <td className="px-3 py-2 text-xs font-bold text-gray-900">
                  IDR {items.reduce((sum, item) => sum + (parseInt(item.quotation_total) || 0), 0).toLocaleString('id-ID')}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    );
  };

  // Table columns
  const columns = [
    {
      name: 'PP No.',
      selector: row => row.nomer,
      sortable: true,
      width: '180px',
      cell: row => (
        <div className="text-xs font-medium text-gray-900">
          {row.nomer}
        </div>
      ),
    },
    {
      name: 'Site',
      selector: row => row.nm_hotel,
      sortable: true,
      width: '120px',
      cell: row => (
        <div className="text-xs">
          {row.nm_hotel}
        </div>
      ),
    },
    {
      name: 'Department',
      selector: row => row.nm_department,
      sortable: true,
      width: '120px',
      cell: row => (
        <div className="text-xs">
          {row.nm_department}
        </div>
      ),
    },
    {
      name: 'Remark',
      selector: row => row.remark,
      wrap: true,
      grow: 2,
     
      cell: row => (
        <div className="text-xs w-full text-gray-700 py-2">
          {row.remark || '-'}
        </div>
      ),
    },
    {
      name: 'Created',
      selector: row => row.created_at,
      sortable: true,
      width: '120px',
      cell: row => (
        <div className="text-xs">
          {Moment(row.created_at).format('DD MMM YYYY')}
        </div>
      ),
    },
    {
      name: 'Need For',
      selector: row => row.date_need,
      sortable: true,
      width: '120px',
      cell: row => (
        <div className="text-xs">
          {Moment(row.date_need).format('DD MMM YYYY')}
        </div>
      ),
    },
    {
      name: 'Status',
      selector: row => row.statusPP,
      sortable: true,
      width: '160px',
      cell: row => {
        // Status codes mapping:
        // 1 = Pengajuan Users (DepH) - Waiting HM
        // 2 = HM Approved - Waiting DOO
        // 22 = HM Reject
        // 3 = DOO Approve
        // 33 = DOO Reject
        const getStatusStyle = (status, code) => {
          // Check by numeric code
          if (code !== undefined) {
            switch(code) {
              case 1: return 'bg-yellow-100 text-yellow-800';  // Pengajuan Users (Waiting HM)
              case 2: return 'bg-indigo-100 text-indigo-800';  // HM Approved (Waiting DOO)
              case 22: return 'bg-red-100 text-red-800';       // HM Reject
              case 3: return 'bg-green-100 text-green-800';    // DOO Approve
              case 33: return 'bg-red-100 text-red-800';       // DOO Reject
              default: return 'bg-gray-100 text-gray-800';
            }
          }
          // Fallback to text matching
          if (status?.includes('Waiting HM')) return 'bg-yellow-100 text-yellow-800';
          if (status?.includes('HM Approve')) return 'bg-indigo-100 text-indigo-800';
          if (status?.includes('Waiting DOO')) return 'bg-indigo-100 text-indigo-800';
          if (status?.includes('DOO Approve')) return 'bg-green-100 text-green-800';
          if (status?.includes('Reject')) return 'bg-red-100 text-red-800';
          return 'bg-gray-100 text-gray-800';
        };

        return (
          <div className="py-2">
            <span className={`inline-flex px-2.5 py-1 rounded-full text-[10px] font-medium whitespace-nowrap ${
              getStatusStyle(row.statusPP, row.stts_pp)
            }`}>
              {row.statusPP}
            </span>
          </div>
        );
      },
    },
    {
      name: 'Action',
      width: '80px',
      center: true,
      cell: row => (
        <div className="flex justify-center">
          <button
            onClick={() => {
              if (row.id_pp) {
                history.push(`/detail/proposal/${row.id_pp}`);
              } else {

              }
            }}
            className="inline-flex items-center justify-center w-8 h-8 text-blue-600 bg-blue-50 rounded-full hover:bg-blue-100 hover:text-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
            title="View Details"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
            </svg>
          </button>
        </div>
      ),
    },
  ];

  // Fetch hotels
  const getListHotelbyUser = useCallback(() => {
    axios
      .post('/list/hotel/by-user', { hcode }, { headers })
      .then(function (response) {

        setHotelList(response.data || []);
      })
      .catch(function (error) {

        setHotelList([]);
      });
  }, [hcode, headers]);

  // Fetch proposals (filters only status codes: 1, 2, 22, 3, 33)
  const getPurchaseProposal = useCallback(() => {
    setLoading(true);

    axios
      .post('/waiting/proposal', 
        { 
          HCode: selectedHotel || hcode, 
          udeptID,
          status: 'waiting'
          // Backend filters: 1 (Waiting HM), 2 (Waiting DOO), 3 (DOO Approve), 22 (HM Reject), 33 (DOO Reject)
        }, 
        { headers }
      )
      .then(function (response) {

        if (response.data) {
          // Flatten the response data as the API returns nested arrays
          const flatData = response.data.flat ? response.data.flat() : Object.values(response.data).flatMap(arr => arr);


          setPurchaseProposal(flatData);
        } else {

          setPurchaseProposal([]);
        }
      })
      .catch(function (error) {


        setPurchaseProposal([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [selectedHotel, hcode, udeptID, headers]);

  useEffect(() => {
    getListHotelbyUser();
    getPurchaseProposal();
  }, []);

  useEffect(() => {
    getPurchaseProposal();
  }, [selectedHotel]);

  // Filter function
  const filteredItems = purchaseProposal.filter(
    item => {
      // Apply status filter first
      if (statusFilter !== null && item.stts_pp !== statusFilter) {
        return false;
      }
      
      // Then apply text filter
      if (!filterText) return true;
      return (
        (item.nomer && item.nomer.toLowerCase().includes(filterText.toLowerCase())) ||
        (item.nm_hotel && item.nm_hotel.toLowerCase().includes(filterText.toLowerCase())) ||
        (item.nm_department && item.nm_department.toLowerCase().includes(filterText.toLowerCase())) ||
        (item.remark && item.remark.toLowerCase().includes(filterText.toLowerCase()))
      );
    }
  );
  
  // Handle status card click
  const handleStatusClick = (status) => {
    // If clicking the same status, clear filter; otherwise set new filter
    setStatusFilter(current => current === status ? null : status);
  };

  // Function to export proposals to Excel with items
  const exportToExcel = async () => {
    setLoading(true);
    
    try {
      // Get status text for title
      const getStatusText = (status) => {
      switch(status) {
        case 1: return 'Waiting HM Approval';
        case 2: return 'Waiting DOO Approval';
        case 22: return 'HM Rejected';
        case 3: return 'DOO Approved';
        case 33: return 'DOO Rejected';
        default: return 'All Status';
      }
    };

    // Get selected hotel name
    const selectedHotelName = selectedHotel 
      ? hotelList.find(h => h.id_hotel == selectedHotel)?.nm_hotel || 'All Hotels'
      : 'All Hotels';

    // Create title
    const title = 'Purchase Proposal';
    const filterInfo = `Hotel: ${selectedHotelName} | Status: ${statusFilter !== null ? getStatusText(statusFilter) : 'All Status'}`;
    const exportDate = `Export Date: ${Moment().format('DD MMMM YYYY HH:mm')}`;

    // Create workbook
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet([]);

    // Add title rows
    XLSX.utils.sheet_add_aoa(ws, [['']], { origin: 'A1' });
    XLSX.utils.sheet_add_aoa(ws, [['']], { origin: 'A2' });
    XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: 'A3' });
    XLSX.utils.sheet_add_aoa(ws, [[filterInfo]], { origin: 'A4' });
    XLSX.utils.sheet_add_aoa(ws, [[exportDate]], { origin: 'A5' });

    // Table headers (row 7)
    const ppHeaders = ['PP No.', 'Site', 'Department', 'Remark', 'Created', 'Need For', 'Status'];
    XLSX.utils.sheet_add_aoa(ws, [ppHeaders], { origin: 'A7' });

    // Item detail headers
    const itemHeaders = ['', 'Item Name', 'Quantity', 'Unit', 'Unit Price (IDR)', 'Total (IDR)', ''];

    // Fetch all items for each PP and build data rows
    let currentRow = 8;
    const allRows = [];
    
    for (const pp of filteredItems) {
      // Add PP header row
      const ppRow = [
        pp.nomer || '',
        pp.nm_hotel || '',
        pp.nm_department || '',
        pp.remark || '',
        pp.created_at ? Moment(pp.created_at).format('DD MMM YYYY') : '',
        pp.date_need ? Moment(pp.date_need).format('DD MMM YYYY') : '',
        getStatusText(pp.stts_pp)
      ];
      allRows.push({ type: 'pp', data: ppRow, rowNum: currentRow });
      currentRow++;

      // Fetch items for this PP
      try {
        const response = await axios.get(`/proposal/items/${pp.id_pp}`, { headers });
        if (response.data && response.data.status === 200 && response.data.data) {
          const items = response.data.data;
          
          // Add item detail headers
          allRows.push({ type: 'itemHeader', data: itemHeaders, rowNum: currentRow });
          currentRow++;
          
          // Add each item
          items.forEach((item) => {
            const itemRow = [
              '',
              item.nama_item || '',
              item.quantity || '',
              item.unit || '',
              item.quotation_price ? parseInt(item.quotation_price).toLocaleString('id-ID') : '',
              item.quotation_total ? parseInt(item.quotation_total).toLocaleString('id-ID') : '',
              ''
            ];
            allRows.push({ type: 'item', data: itemRow, rowNum: currentRow });
            currentRow++;
          });

          // Add total row
          const total = items.reduce((sum, item) => sum + (parseInt(item.quotation_total) || 0), 0);
          allRows.push({ 
            type: 'total', 
            data: ['', '', '', '', 'TOTAL:', total.toLocaleString('id-ID'), ''], 
            rowNum: currentRow 
          });
          currentRow++;
        }
      } catch (error) {

      }

      // Add empty row after each PP group
      allRows.push({ type: 'empty', data: ['', '', '', '', '', '', ''], rowNum: currentRow });
      currentRow++;
    }

    // Add all rows to worksheet
    allRows.forEach(rowData => {
      XLSX.utils.sheet_add_aoa(ws, [rowData.data], { origin: `A${rowData.rowNum}` });
    });

    // Set column widths
    ws['!cols'] = [
      { wch: 20 }, // PP No.
      { wch: 20 }, // Site
      { wch: 20 }, // Department
      { wch: 40 }, // Remark
      { wch: 15 }, // Created
      { wch: 15 }, // Need For
      { wch: 25 }  // Status
    ];

    // Freeze panes at row 8 (after header row 7)
    if (!ws['!views']) ws['!views'] = [{}];
    ws['!views'][0] = { state: 'frozen', ySplit: 7 };

    // Apply borders and styling
    const borderStyle = {
      top: { style: 'thin' },
      bottom: { style: 'thin' },
      left: { style: 'thin' },
      right: { style: 'thin' }
    };

    const lastRow = currentRow - 1;
    
    // Style all cells
    for (let R = 6; R <= lastRow; R++) {
      for (let C = 0; C <= 6; C++) {
        const cellAddress = XLSX.utils.encode_cell({ r: R, c: C });
        if (!ws[cellAddress]) {
          ws[cellAddress] = { t: 's', v: '' };
        }
        if (!ws[cellAddress].s) {
          ws[cellAddress].s = {};
        }
        
        // Find row type
        const rowData = allRows.find(r => r.rowNum === R + 1);
        
        // Skip borders for empty rows
        if (rowData && rowData.type !== 'empty') {
          ws[cellAddress].s.border = borderStyle;
        }
        
        ws[cellAddress].s.alignment = { 
          vertical: 'center', 
          horizontal: 'left',
          wrapText: true 
        };

        // Style PP header row (main header)
        if (R === 6) {
          ws[cellAddress].s.font = { bold: true };
          ws[cellAddress].s.fill = { patternType: 'solid', fgColor: { rgb: 'E0E0E0' } };
          ws[cellAddress].s.alignment = { vertical: 'center', horizontal: 'center', wrapText: true };
        }

        // Style PP data rows (Green background)
        if (rowData && rowData.type === 'pp') {
          ws[cellAddress].s.fill = { patternType: 'solid', fgColor: { rgb: 'D1FAE5' } }; // Light green
          ws[cellAddress].s.font = { bold: true };
        }

        // Style item header rows (Blue background)
        if (rowData && rowData.type === 'itemHeader') {
          ws[cellAddress].s.fill = { patternType: 'solid', fgColor: { rgb: '93C5FD' } }; // Blue
          ws[cellAddress].s.font = { bold: true, sz: 10 };
          ws[cellAddress].s.alignment = { vertical: 'center', horizontal: 'center', wrapText: true };
        }

        // Style item detail rows (Light blue background)
        if (rowData && rowData.type === 'item') {
          ws[cellAddress].s.fill = { patternType: 'solid', fgColor: { rgb: 'DBEAFE' } }; // Light blue
        }

        // Style total rows
        if (rowData && rowData.type === 'total') {
          ws[cellAddress].s.font = { bold: true };
          ws[cellAddress].s.fill = { patternType: 'solid', fgColor: { rgb: 'FFEB9C' } };
        }
      }
    }

    // Style title rows
    ['A3', 'A4', 'A5'].forEach((cell, idx) => {
      if (ws[cell]) {
        ws[cell].s = {
          font: { bold: idx === 0, sz: [14, 12, 11][idx] },
          alignment: { vertical: 'center', horizontal: 'left' }
        };
      }
    });

      XLSX.utils.book_append_sheet(wb, ws, 'Purchase Proposals');
      const fileName = `Purchase_Proposal_${Moment().format('YYYYMMDD_HHmmss')}.xlsx`;
      XLSX.writeFile(wb, fileName);
      
      Swal.fire({
        icon: 'success',
        title: 'Export Complete!',
        text: 'Excel file has been downloaded successfully.',
        timer: 2000,
        showConfirmButton: false
      });
    } catch (error) {

      Swal.fire({
        icon: 'error',
        title: 'Export Failed',
        text: 'Failed to export data. Please try again.',
      });
    } finally {
      setLoading(false);
    }
  };

  const subHeaderComponent = (
    <div className="flex items-center justify-between w-full py-4">
      <div className="flex items-center space-x-4">
        {/* Search Input */}
        <div className="relative">
          <input
            type="text"
            placeholder="Search ..."
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-accent-rose focus:border-transparent"
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
          />
          <svg className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
        </div>

        {/* Active Filter Indicator */}
        {statusFilter !== null && (
          <div className="flex items-center px-3 py-2 bg-purple-100 rounded-lg">
            <span className="text-xs text-purple-700 font-medium mr-2">
              Filter: {
                statusFilter === 1 ? 'Waiting HM' :
                statusFilter === 2 ? 'Waiting DOO' :
                statusFilter === 3 ? 'DOO Approved' :
                statusFilter === 22 ? 'HM Rejected' :
                statusFilter === 33 ? 'DOO Rejected' : ''
              }
            </span>
            <button
              onClick={() => setStatusFilter(null)}
              className="text-purple-700 hover:text-purple-900"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        )}

        {/* Hotel Filter - Show when user has access to multiple hotels */}
        {hotelList && hotelList.length > 0 && (() => {
          // Flatten the hotel list in case it's nested
          const flatHotels = hotelList.flat();
          // Only show filter if there's more than 1 hotel
          if (flatHotels.length <= 1) return null;
          
          return (
            <select
              className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              value={selectedHotel}
              onChange={(e) => setSelectedHotel(e.target.value)}
            >
              <option value="">All Hotels</option>
              {flatHotels.map((hotel) => (
                <option key={hotel.id_hotel} value={hotel.id_hotel}>
                  {hotel.nm_hotel} ({hotel.code_hotel})
                </option>
              ))}
            </select>
          );
        })()}
      </div>

      <div className="flex items-center space-x-4">
        {/* Export Excel Button */}
        <button
          onClick={exportToExcel}
          disabled={loading}
          className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? (
            <>
              <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span>Exporting...</span>
            </>
          ) : (
            <>
              <Download className="w-5 h-5" />
              <span>Export Excel</span>
            </>
          )}
        </button>

        {/* Refresh Button */}
        <button
          onClick={() => getPurchaseProposal()}
          className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center space-x-2"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          <span>Refresh</span>
        </button>
      </div>
    </div>
  );

  return (
    <Layout title="Purchase Proposals" subtitle="Manage and track purchase proposals">
      <div className="p-8">
        
        {/* Stats Cards - Individual Status Cards (Clickable to filter) */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          {/* Total Active Proposals (excluding rejected) */}
          <div 
            onClick={() => handleStatusClick(null)}
            className={`relative bg-gradient-to-br from-primary-dark to-secondary-dark dark:bg-gray-600 rounded-xl p-3 shadow-lg cursor-pointer transition-all duration-300 hover:shadow-2xl hover:scale-105 overflow-hidden group ${
              statusFilter === null ? 'ring-2 ring-white ring-offset-2' : ''
            }`}
          >
            <div className="absolute -right-2 opacity-10">
              <FileText className="w-20 h-20 text-white" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <p className="text-2xl font-bold text-white">
                  {purchaseProposal.filter(p => p.stts_pp !== 22 && p.stts_pp !== 33).length}
                </p>
                {statusFilter === null && (
                  <CheckCircle2 className="w-5 h-5 text-white" />
                )}
              </div>
              <p className="text-xs text-white/70 mt-1">Active</p>
              <p className="text-[10px] text-white/60 mt-0.5">Non-Rejected</p>
            </div>
          </div>

          {/* Status 1 - Pengajuan Users (Waiting HM) */}
          <div 
            onClick={() => handleStatusClick(1)}
            className={`relative bg-gradient-to-br from-secondary-dark to-accent-rose dark:bg-gray-600 rounded-xl p-3 shadow-lg cursor-pointer transition-all duration-300 hover:shadow-2xl hover:scale-105 overflow-hidden group ${
              statusFilter === 1 ? 'ring-2 ring-white ring-offset-2' : ''
            }`}
          >
            <div className="absolute -right-2 opacity-10">
              <Clock className="w-20 h-20 text-white" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <p className="text-2xl font-bold text-white">
                  {purchaseProposal.filter(p => p.stts_pp === 1).length}
                </p>
                {statusFilter === 1 && (
                  <CheckCircle2 className="w-5 h-5 text-white" />
                )}
              </div>
              <p className="text-xs text-white/70 mt-1">Waiting HM</p>
              <p className="text-[10px] text-white/60 mt-0.5">Pengajuan Users</p>
            </div>
          </div>

          {/* Status 2 - HM Approved (Waiting DOO) */}
          <div 
            onClick={() => handleStatusClick(2)}
            className={`relative bg-gradient-to-br from-accent-rose to-primary-dark dark:bg-gray-600 rounded-xl p-3 shadow-lg cursor-pointer transition-all duration-300 hover:shadow-2xl hover:scale-105 overflow-hidden group ${
              statusFilter === 2 ? 'ring-2 ring-white ring-offset-2' : ''
            }`}
          >
            <div className="absolute -right-2 opacity-10">
              <ClipboardCheck className="w-20 h-20 text-white" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <p className="text-2xl font-bold text-white">
                  {purchaseProposal.filter(p => p.stts_pp === 2).length}
                </p>
                {statusFilter === 2 && (
                  <CheckCircle2 className="w-5 h-5 text-white" />
                )}
              </div>
              <p className="text-xs text-white/70 mt-1">Waiting DOO</p>
              <p className="text-[10px] text-white/60 mt-0.5">HM Approved</p>
            </div>
          </div>

          {/* Status 3 - DOO Approve */}
          <div 
            onClick={() => handleStatusClick(3)}
            className={`relative bg-gradient-to-br from-primary-dark to-accent-rose dark:bg-gray-600 rounded-xl p-3 shadow-lg cursor-pointer transition-all duration-300 hover:shadow-2xl hover:scale-105 overflow-hidden group ${
              statusFilter === 3 ? 'ring-2 ring-white ring-offset-2' : ''
            }`}
          >
            <div className="absolute -right-2 opacity-10">
              <CheckCircle className="w-20 h-20 text-white" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <p className="text-2xl font-bold text-white">
                  {purchaseProposal.filter(p => p.stts_pp === 3).length}
                </p>
                {statusFilter === 3 && (
                  <CheckCircle2 className="w-5 h-5 text-white" />
                )}
              </div>
              <p className="text-xs text-white/70 mt-1">Waiting Purchasing</p>
              <p className="text-[10px] text-white/60 mt-0.5">DOO Approve</p>
            </div>
          </div>

          {/* Status 22 - HM Reject */}
          <div 
            onClick={() => handleStatusClick(22)}
            className={`relative bg-gradient-to-br from-accent-rose to-secondary-dark dark:bg-gray-600 rounded-xl p-3 shadow-lg cursor-pointer transition-all duration-300 hover:shadow-2xl hover:scale-105 overflow-hidden group ${
              statusFilter === 22 ? 'ring-2 ring-white ring-offset-2' : ''
            }`}
          >
            <div className="absolute -right-2 opacity-10">
              <XCircle className="w-20 h-20 text-white" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <p className="text-2xl font-bold text-white">
                  {purchaseProposal.filter(p => p.stts_pp === 22).length}
                </p>
                {statusFilter === 22 && (
                  <CheckCircle2 className="w-5 h-5 text-white" />
                )}
              </div>
              <p className="text-xs text-white/70 mt-1">HM Reject</p>
              <p className="text-[10px] text-white/60 mt-0.5">Rejected by HM</p>
            </div>
          </div>

          {/* Status 33 - DOO Reject */}
          <div 
            onClick={() => handleStatusClick(33)}
            className={`relative bg-gradient-to-br from-secondary-dark to-primary-dark rounded-xl p-3 shadow-lg cursor-pointer transition-all duration-300 hover:shadow-2xl hover:scale-105 overflow-hidden group ${
              statusFilter === 33 ? 'ring-2 ring-white ring-offset-2' : ''
            }`}
          >
            <div className="absolute -right-2 opacity-10">
              <AlertCircle className="w-20 h-20 text-white" />
            </div>
            <div className="relative z-10">
              <div className="flex items-center justify-between">
                <p className="text-2xl font-bold text-white">
                  {purchaseProposal.filter(p => p.stts_pp === 33).length}
                </p>
                {statusFilter === 33 && (
                  <CheckCircle2 className="w-5 h-5 text-white" />
                )}
              </div>
              <p className="text-xs text-white/70 mt-1">DOO Reject</p>
              <p className="text-[10px] text-white/60 mt-0.5">Rejected by DOO</p>
            </div>
          </div>
        </div>

        {/* Data Table */}
        <div className="bg-white rounded-xl p-2 shadow-sm overflow-hidden">
          <DataTable
            columns={columns}
            data={filteredItems}
            customStyles={customStyles}
            pagination
            paginationPerPage={10}
            paginationRowsPerPageOptions={[10, 20, 30, 50]}
            paginationComponentOptions={{
              rowsPerPageText: 'Rows per page:',
              rangeSeparatorText: 'of',
              noRowsPerPage: false,
              selectAllRowsItem: false,
              selectAllRowsItemText: 'All'
            }}
            subHeader
            subHeaderComponent={subHeaderComponent}
            persistTableHead
            responsive
            expandableRows
            expandableRowsComponent={ExpandedComponent}
            onRowExpandToggled={(expanded, row) => {
              if (expanded) {
                fetchProposalItems(row.id_pp);
              }
            }}
            expandableIcon={{
              collapsed: (
                <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              ),
              expanded: (
                <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              )
            }}
            progressPending={loading}
            progressComponent={
              <div className="flex items-center justify-center p-12">
                <div className="flex flex-col items-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
                  <p className="text-gray-600">Loading proposals...</p>
                </div>
              </div>
            }
            noDataComponent={
              <div className="flex flex-col items-center justify-center p-12">
                <svg className="w-16 h-16 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p className="text-gray-500 text-lg mb-2">No proposals found</p>
                <p className="text-gray-400 text-sm">Try adjusting your filters or create a new proposal</p>
              </div>
            }
          />
        </div>
      </div>
    </Layout>
  );
}

export default Proposal;
